/**
 * Piece definitions and utilities
 */

import type { Piece, PieceType } from './types';
import { PieceType as PT, Color as C } from './types';

/**
 * Returns the Unicode symbol for a piece
 */
export function getPieceSymbol(piece: Piece): string {
  const symbols = {
    [C.White]: ['♙', '♘', '♗', '♖', '♕', '♔'],
    [C.Black]: ['♟', '♞', '♝', '♜', '♛', '♚'],
  };
  return symbols[piece.color][piece.type];
}

/**
 * Returns the FEN character for a piece
 */
export function getPieceFEN(piece: Piece): string {
  const chars = ['P', 'N', 'B', 'R', 'Q', 'K'];
  const char = chars[piece.type];
  return piece.color === C.White ? char : char.toLowerCase();
}

/**
 * Creates a piece from a FEN character
 */
export function pieceFromFEN(char: string): Piece | null {
  const typeMap: Record<string, PieceType> = {
    'p': PT.Pawn, 'n': PT.Knight, 'b': PT.Bishop,
    'r': PT.Rook, 'q': PT.Queen, 'k': PT.King,
  };

  const lower = char.toLowerCase();
  if (!(lower in typeMap)) return null;

  return {
    type: typeMap[lower],
    color: char === lower ? C.Black : C.White,
  };
}

/**
 * Checks if a piece is a sliding piece (bishop, rook, queen)
 */
export function isSlidingPiece(type: PieceType): boolean {
  return type === PT.Bishop || type === PT.Rook || type === PT.Queen;
}

/**
 * Checks if a piece is a jumping piece (knight, king)
 */
export function isJumpingPiece(type: PieceType): boolean {
  return type === PT.Knight || type === PT.King;
}

/**
 * Returns the relative value of a piece (for evaluation)
 */
export function getPieceValue(type: PieceType): number {
  const values = [1, 3, 3, 5, 9, 0]; // Pawn, Knight, Bishop, Rook, Queen, King
  return values[type];
}
